import {
  fetchDataBlockSSRFromDiscoveryV2,
  fetchSingleDataBlockSSRFromDiscoveryV2,
  ServiceContainer,
  transformBlock,
} from 'helpers';
import { logger } from '@teko/log';
import {
  BlockType,
  DiscoveryBlock,
  PageTypeEnum,
  PersonalizeBlockParams,
} from '@teko/discovery';
import { Router } from 'next/router';
import {
  extractSortQuery,
  getActiveSortOption,
} from 'modules/search-page/shared';

type ParsedUrlQuery = typeof Router.prototype.query;
export const getBlockParams = (
  query: ParsedUrlQuery,
): PersonalizeBlockParams => {
  let blockParams: PersonalizeBlockParams = {
    pageTypeEnum: PageTypeEnum.CATEGORY,
  };
  const { categoryId, sellerId, brandId, sellerCategoryId } = query;
  if (categoryId) {
    blockParams = {
      pageTypeEnum: PageTypeEnum.CATEGORY,
      category: categoryId as string,
    };
  } else if (sellerId) {
    blockParams = {
      pageTypeEnum: PageTypeEnum.SELLER,
      seller: sellerId as string,
    };
  } else if (brandId) {
    blockParams = {
      pageTypeEnum: PageTypeEnum.BRAND,
      brand: brandId as string,
    };
  } else if (sellerCategoryId) {
    blockParams = {
      pageTypeEnum: PageTypeEnum.CATEGORY,
      sellerCategory: sellerCategoryId as string,
    };
  }
  return blockParams;
};

export const getServerSidePropsBlockPage: GetServerSideProps = async (
  context,
) => {
  const query = context.query;
  const { tenantEnv, isMobile } = context.req;
  const { discoveryService } = new ServiceContainer(tenantEnv);
  // Logic auto gen slug can be found in: shop-front/helpers/index.ts > buildSlugUrl
  let canonical = '';
  const blockParams = getBlockParams(query);
  const { brandId } = query;

  const activeSortOption = getActiveSortOption(
    query,
    tenantEnv.sortByCreatedAt,
    tenantEnv.defaultSortOptionCode,
  );
  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];

  try {
    const data = await discoveryService.getBlock({
      ...blockParams,
    });

    const blocks: DiscoveryBlock[] =
      data?.result?.blocks?.map(transformBlock) || [];
    // Override canonical
    if (data?.result?.seoInfo?.canonical) {
      canonical = data?.result?.seoInfo?.canonical;
    }

    if (!canonical && !!brandId) {
      // set canonical to only path of the current request
      // ignore query parameters
      canonical = `/${brandId}-brand.${brandId}`;
    }
    const serverData = {
      blocks: blocks || [],
      seoInfo: data?.result?.seoInfo
        ? { ...data?.result.seoInfo, canonical }
        : {},
    };

    // handle default template
    // which would include 2 blocks MAIN_SKU_LIST && LIST_SKU_MOBILE
    if (
      blocks.length === 2 &&
      blocks.find((b) => b.type === BlockType.MAIN_SKU_LIST) &&
      blocks.find((b) => b.type === BlockType.LIST_SKU_MOBILE)
    ) {
      // SSR filter block for desktop default template
      if (!isMobile) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const mainSkuBlock = blocks.find(
          (b) => b.type === BlockType.MAIN_SKU_LIST,
        )!;

        const resp = await fetchSingleDataBlockSSRFromDiscoveryV2(
          context,
          mainSkuBlock,
          true,
          {
            sort,
            order,
          },
        );

        if (!resp.ssrProducts?.length) {
          return { notFound: true };
        }

        return {
          props: {
            ...serverData,
            mapSSRProducts: { [mainSkuBlock.id]: resp.ssrProducts },
            cookedFilters: resp.cookFilters || null,
            filters: resp.filters || null,
            filterPrice: resp.filterPrice || null,
          },
        };
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const listSkuMobile = blocks.find(
          (b) => b.type === BlockType.LIST_SKU_MOBILE,
        )!;
        const res = await fetchSingleDataBlockSSRFromDiscoveryV2(
          context,
          listSkuMobile,
          false,
          { sort, order },
        );

        // count the number of products and return 404 if it has nothing
        if (!res.ssrProducts?.length) {
          return {
            notFound: true,
          };
        }

        return {
          props: {
            ...serverData,
            mapSSRProducts: { [listSkuMobile.id]: res.ssrProducts || [] },
          },
        };
      }
    }

    const res = await fetchDataBlockSSRFromDiscoveryV2(context, blocks);
    return {
      props: {
        ...serverData,
        mapSSRProducts: res || {},
      },
    };
  } catch (e) {
    logger.error(context.req, e);
    return {
      props: {
        blocks: [],
        cookedFilters: [],
      },
    };
  }
};
